import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  ChevronRight,
  BrainCircuit,
  Timer,
  Hash,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { useTradeJournal } from "../../context/TradeJournalContext";
import { BehaviorEntry } from "../../types/tradingJournal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import { useToast } from "../../hooks/use-toast";

interface BehaviorJournalFormProps {
  onSubmit: () => void;
  editingEntry?: BehaviorEntry | null;
}

type FormValues = {
  date: string;
  dailyPnL: string;
  followedPlan: "Yes" | "No" | "Partially";
  cutLossesQuickly: "Yes" | "No" | "Partially";
  overtraded: "Yes" | "No" | "Partially";
  emotionallyCalm: "Yes" | "No" | "Partially";
  tradedOnSetup: "Setup" | "Instinct" | "Both";
  notes: string;
  tradingTimes: string[];
  numberOfTrades: string;
  wasRushing: "Yes" | "No" | "Somewhat";
  revengeTrading: "Yes" | "No" | "Not Sure";
};

const VALID_TRADING_TIMES = ["Morning", "Midday", "After 2 PM"] as const;
type ValidTradingTime = (typeof VALID_TRADING_TIMES)[number];

const BehaviorJournalForm = ({
  onSubmit,
  editingEntry,
}: BehaviorJournalFormProps) => {
  const { addBehaviorEntry, updateBehaviorEntry, behaviorEntries } =
    useTradeJournal();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      date: new Date().toISOString().split("T")[0],
      dailyPnL: "0",
      followedPlan: "Yes",
      cutLossesQuickly: "Yes",
      overtraded: "No",
      emotionallyCalm: "Yes",
      tradedOnSetup: "Setup",
      notes: "",
      tradingTimes: [],
      numberOfTrades: "0",
      wasRushing: "No",
      revengeTrading: "No",
    },
  });

  useEffect(() => {
    if (editingEntry) {
      setValue("date", editingEntry.date);
      setValue("dailyPnL", editingEntry.dailyPnL.toString());
      setValue("followedPlan", editingEntry.followedPlan);
      setValue("cutLossesQuickly", editingEntry.cutLossesQuickly);
      setValue("overtraded", editingEntry.overtraded);
      setValue("emotionallyCalm", editingEntry.emotionallyCalm);
      setValue("tradedOnSetup", editingEntry.tradedOnSetup);
      if (editingEntry.notes) {
        setValue("notes", editingEntry.notes);
      }
      if (editingEntry.tradingTimes) {
        setValue("tradingTimes", editingEntry.tradingTimes);
      }
      if (editingEntry.numberOfTrades) {
        setValue("numberOfTrades", editingEntry.numberOfTrades.toString());
      }
      if (editingEntry.wasRushing) {
        setValue("wasRushing", editingEntry.wasRushing);
      }
      if (editingEntry.revengeTrading) {
        setValue("revengeTrading", editingEntry.revengeTrading);
      }
    }
  }, [editingEntry, setValue]);

  const processSubmit = (data: FormValues) => {
    const pnl = parseFloat(data.dailyPnL);
    const numTrades = parseInt(data.numberOfTrades) || 0;

    const validTradingTimes = data.tradingTimes.filter(
      (time): time is ValidTradingTime =>
        VALID_TRADING_TIMES.includes(time as ValidTradingTime)
    );

    const behaviorEntry: Omit<BehaviorEntry, "id" | "createdAt" | "updatedAt"> =
      {
        date: data.date,
        dailyPnL: pnl,
        followedPlan: data.followedPlan,
        cutLossesQuickly: data.cutLossesQuickly,
        overtraded: data.overtraded,
        emotionallyCalm: data.emotionallyCalm,
        tradedOnSetup: data.tradedOnSetup,
        notes: data.notes,
        tradingTimes: validTradingTimes,
        numberOfTrades: numTrades,
        wasRushing: data.wasRushing,
        revengeTrading: data.revengeTrading,
      };

    if (!editingEntry && behaviorEntries.some((e) => e.date === data.date)) {
      toast({
        title: "Entry already exists",
        description:
          "An entry for this date already exists. Please edit it instead.",
        variant: "destructive",
      });
      return;
    }

    if (editingEntry) {
      updateBehaviorEntry(editingEntry.id, behaviorEntry);
    } else {
      addBehaviorEntry(behaviorEntry);
    }

    reset();
    onSubmit();
  };

  const dailyPnL = watch("dailyPnL");
  const tradingTimes = watch("tradingTimes") || [];

  const toggleTradingTime = (time: ValidTradingTime) => {
    const currentTimes = [...tradingTimes];
    const timeIndex = currentTimes.indexOf(time);

    if (timeIndex === -1) {
      currentTimes.push(time);
    } else {
      currentTimes.splice(timeIndex, 1);
    }

    setValue("tradingTimes", currentTimes);
  };

  return (
    <Card className="intellectia-card shadow-lg">
      <CardHeader>
        <CardTitle>
          <div className="flex items-center gap-2">
            <BrainCircuit className="h-5 w-5 text-market-teal" />
            {editingEntry ? "Edit Behavior Journal" : "New Behavior Journal"}
          </div>
        </CardTitle>
        <CardDescription>
          Track your daily trading psychology and behaviors
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          id="behavior-form"
          onSubmit={handleSubmit(processSubmit)}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  {...register("date", { required: true })}
                  className="bg-white/5 border-white/10"
                />
                {errors.date && (
                  <p className="text-destructive text-sm">Date is required</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="dailyPnL">Daily P&L</Label>
                <Input
                  id="dailyPnL"
                  type="text" // was number
                  inputMode="decimal" // gives numeric keyboard on mobile
                  placeholder="0.00"
                  {...register("dailyPnL", {
                    required: "Daily P&L is required",
                    validate: (val) =>
                      /^[-+]?\d+(\.\d+)?$/.test(val) ||
                      "Only one +/– at start, then digits and optional decimal",
                  })}
                  className={`bg-white/5 border-white/10 ${
                    /^-/.test(watch("dailyPnL"))
                      ? "text-red-400"
                      : "text-green-400"
                  }`}
                  onKeyDown={(e) => {
                    const { key, currentTarget } = e;
                    const hasSign = /^[+-]/.test(currentTarget.value);
                    // block + or - unless at pos 0 and no sign yet
                    if (
                      (key === "+" || key === "-") &&
                      (currentTarget.selectionStart !== 0 || hasSign)
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
                {errors.dailyPnL && (
                  <p className="text-darkMode-red text-sm ">
                    {errors.dailyPnL.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="followedPlan">
                  Did you follow your trading plan today?
                </Label>
                <Select
                  onValueChange={(value: "Yes" | "No" | "Partially") =>
                    setValue("followedPlan", value)
                  }
                  defaultValue={watch("followedPlan")}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="intellectia-card">
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="Partially">Partially</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cutLossesQuickly">
                  Did you cut losses quickly?
                </Label>
                <Select
                  onValueChange={(value: "Yes" | "No" | "Partially") =>
                    setValue("cutLossesQuickly", value)
                  }
                  defaultValue={watch("cutLossesQuickly")}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="intellectia-card">
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="Partially">Partially</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Timer className="h-4 w-4 text-market-teal" />
                  <Label>What time(s) did you trade the most today?</Label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="morning"
                      checked={tradingTimes.includes("Morning")}
                      onCheckedChange={() => toggleTradingTime("Morning")}
                      className="data-[state=checked]:bg-market-teal"
                    />
                    <label htmlFor="morning" className="text-sm cursor-pointer">
                      Morning
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="midday"
                      checked={tradingTimes.includes("Midday")}
                      onCheckedChange={() => toggleTradingTime("Midday")}
                      className="data-[state=checked]:bg-market-teal"
                    />
                    <label htmlFor="midday" className="text-sm cursor-pointer">
                      Midday
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="afternoon"
                      checked={tradingTimes.includes("After 2 PM")}
                      onCheckedChange={() => toggleTradingTime("After 2 PM")}
                      className="data-[state=checked]:bg-market-teal"
                    />
                    <label
                      htmlFor="afternoon"
                      className="text-sm cursor-pointer"
                    >
                      After 2 PM
                    </label>
                  </div>
                </div>
                <p className="text-xs text-white/40">
                  Helps track if certain periods cause more losses/gains
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Hash className="h-4 w-4 text-market-teal" />
                  <Label htmlFor="numberOfTrades">
                    How many trades did you take today?
                  </Label>
                </div>
                <Input
                  id="numberOfTrades"
                  type="number"
                  min="0"
                  placeholder="0"
                  {...register("numberOfTrades")}
                  className="bg-white/5 border-white/10"
                />
                <p className="text-xs text-white/40">
                  Helps evaluate consistency and potential overtrading
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="overtraded">Did you overtrade today?</Label>
                <Select
                  onValueChange={(value: "Yes" | "No" | "Partially") =>
                    setValue("overtraded", value)
                  }
                  defaultValue={watch("overtraded")}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="intellectia-card">
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="Partially">Partially</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="emotionallyCalm">
                  Were you emotionally calm?
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <input
                      type="radio"
                      id="emotionally-yes"
                      value="Yes"
                      {...register("emotionallyCalm")}
                      className="sr-only peer"
                    />
                    <label
                      htmlFor="emotionally-yes"
                      className="flex items-center justify-center p-2 border border-white/10 bg-white/5 rounded-md peer-checked:bg-green-900/30 peer-checked:text-green-400 peer-checked:border-green-600/30 cursor-pointer"
                    >
                      Yes
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="emotionally-partially"
                      value="Partially"
                      {...register("emotionallyCalm")}
                      className="sr-only peer"
                    />
                    <label
                      htmlFor="emotionally-partially"
                      className="flex items-center justify-center p-2 border border-white/10 bg-white/5 rounded-md peer-checked:bg-yellow-900/30 peer-checked:text-yellow-400 peer-checked:border-yellow-600/30 cursor-pointer"
                    >
                      Partially
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="emotionally-no"
                      value="No"
                      {...register("emotionallyCalm")}
                      className="sr-only peer"
                    />
                    <label
                      htmlFor="emotionally-no"
                      className="flex items-center justify-center p-2 border border-white/10 bg-white/5 rounded-md peer-checked:bg-red-900/30 peer-checked:text-red-400 peer-checked:border-red-600/30 cursor-pointer"
                    >
                      No
                    </label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tradedOnSetup">
                  Did you take trades based on setup or instinct?
                </Label>
                <Select
                  onValueChange={(value: "Setup" | "Instinct" | "Both") =>
                    setValue("tradedOnSetup", value)
                  }
                  defaultValue={watch("tradedOnSetup")}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="intellectia-card">
                    <SelectItem value="Setup">Setup</SelectItem>
                    <SelectItem value="Instinct">Instinct</SelectItem>
                    <SelectItem value="Both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-market-teal" />
                  <Label>Were you in a rush while trading today?</Label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <input
                      type="radio"
                      id="rushing-yes"
                      value="Yes"
                      {...register("wasRushing")}
                      className="sr-only peer"
                    />
                    <label
                      htmlFor="rushing-yes"
                      className="flex items-center justify-center p-2 border border-white/10 bg-white/5 rounded-md peer-checked:bg-red-900/30 peer-checked:text-red-400 peer-checked:border-red-600/30 cursor-pointer"
                    >
                      Yes
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="rushing-somewhat"
                      value="Somewhat"
                      {...register("wasRushing")}
                      className="sr-only peer"
                    />
                    <label
                      htmlFor="rushing-somewhat"
                      className="flex items-center justify-center p-2 border border-white/10 bg-white/5 rounded-md peer-checked:bg-yellow-900/30 peer-checked:text-yellow-400 peer-checked:border-yellow-600/30 cursor-pointer"
                    >
                      Somewhat
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="rushing-no"
                      value="No"
                      {...register("wasRushing")}
                      className="sr-only peer"
                    />
                    <label
                      htmlFor="rushing-no"
                      className="flex items-center justify-center p-2 border border-white/10 bg-white/5 rounded-md peer-checked:bg-green-900/30 peer-checked:text-green-400 peer-checked:border-green-600/30 cursor-pointer"
                    >
                      No
                    </label>
                  </div>
                </div>
                <p className="text-xs text-white/40">
                  Helps identify impatience that often leads to poor execution
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <RotateCcw className="h-4 w-4 text-market-teal" />
                  <Label>Did you revenge trade today?</Label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <input
                      type="radio"
                      id="revenge-yes"
                      value="Yes"
                      {...register("revengeTrading")}
                      className="sr-only peer"
                    />
                    <label
                      htmlFor="revenge-yes"
                      className="flex items-center justify-center p-2 border border-white/10 bg-white/5 rounded-md peer-checked:bg-red-900/30 peer-checked:text-red-400 peer-checked:border-red-600/30 cursor-pointer"
                    >
                      Yes
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="revenge-notsure"
                      value="Not Sure"
                      {...register("revengeTrading")}
                      className="sr-only peer"
                    />
                    <label
                      htmlFor="revenge-notsure"
                      className="flex items-center justify-center p-2 border border-white/10 bg-white/5 rounded-md peer-checked:bg-yellow-900/30 peer-checked:text-yellow-400 peer-checked:border-yellow-600/30 cursor-pointer"
                    >
                      Not Sure
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="revenge-no"
                      value="No"
                      {...register("revengeTrading")}
                      className="sr-only peer"
                    />
                    <label
                      htmlFor="revenge-no"
                      className="flex items-center justify-center p-2 border border-white/10 bg-white/5 rounded-md peer-checked:bg-green-900/30 peer-checked:text-green-400 peer-checked:border-green-600/30 cursor-pointer"
                    >
                      No
                    </label>
                  </div>
                </div>
                <p className="text-xs text-white/40">
                  Critical for identifying psychological slip-ups on loss days
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional thoughts about today's trading..."
                  {...register("notes")}
                  className="bg-white/5 border-white/10 resize-none h-24"
                />
              </div>
            </div>
          </div>

          {parseFloat(dailyPnL) !== 0 && (
            <div
              className={`p-4 rounded-xl ${
                parseFloat(dailyPnL) >= 0
                  ? "bg-green-950/20 border border-green-500/30"
                  : "bg-red-950/20 border border-red-500/30"
              }`}
            >
              <div className="flex justify-between items-center">
                <span className="text-white/70">Daily P&L:</span>
                <span
                  className={
                    parseFloat(dailyPnL) >= 0
                      ? "text-green-400"
                      : "text-red-400"
                  }
                >
                  ₹{" "}
                  {isNaN(parseFloat(dailyPnL))
                    ? "0.00"
                    : parseFloat(dailyPnL).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </form>
      </CardContent>
      <CardFooter className="flex justify-end gap-4">
        <Button
          variant="outline"
          className="bg-white/5 border-white/10"
          onClick={() => {
            reset();
            onSubmit();
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="behavior-form"
          className="intellectia-button text-black"
        >
          {editingEntry ? "Update Entry" : "Save Entry"}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </CardFooter>
    </Card>
  );
};

export default BehaviorJournalForm;
